'use client';

import { useEffect, useState } from 'react';

interface Review {
  name: string;
  rating: number;
  text: string;
  date: string;
  initials: string;
  photo: string | null;
}

interface PlacesData {
  name: string;
  rating: number;
  total: number;
  reviews: Review[];
}

const fallbackReviews: Review[] = [
{
  name: 'Sarah M.',
  rating: 5,
  text: 'Absolutely incredible work! My car looks brand new. The attention to detail is unmatched — every inch was spotless. Will definitely be a returning customer!',
  date: '2 weeks ago',
  initials: 'SM',
  photo: null
},
{
  name: 'Carlos R.',
  rating: 5,
  text: 'Figure 8 Detailing is the real deal. They came to my house, were super professional, and my truck has never looked this clean. Highly recommend the Full Signature package!',
  date: '1 month ago',
  initials: 'CR',
  photo: null
},
{
  name: 'Jessica T.',
  rating: 5,
  text: 'I got the interior restoration and I am blown away. Stains I thought were permanent are completely gone. The whole car smells amazing. 10/10 service!',
  date: '3 weeks ago',
  initials: 'JT',
  photo: null
},
{
  name: 'Mike D.',
  rating: 5,
  text: 'Best mobile detailing in San Diego, no question. Super convenient, great price, and the results speak for themselves. My Porsche looks showroom ready.',
  date: '1 month ago',
  initials: 'MD',
  photo: null
},
{
  name: 'Khai J.',
  rating: 5,
  text: 'We were so happy with the service. Please come back and detail the Honda! Seriously though, 5 stars all the way. Professional, thorough, and friendly.',
  date: '2 months ago',
  initials: 'KJ',
  photo: null
},
{
  name: 'Amanda L.',
  rating: 5,
  text: 'Booked the Full Essential and it was worth every penny. They were on time, efficient, and the car looked amazing. Easy to book and great communication.',
  date: '3 weeks ago',
  initials: 'AL',
  photo: null
}];


function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) =>
      <svg key={i} className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-silver-dark'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

const GoogleIcon = () =>
<svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>;


export default function Reviews() {
  const [data, setData] = useState<PlacesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    fetch('/api/reviews').
    then((res) => res.json()).
    then((json) => {
      if (json.reviews && json.reviews.length > 0) {
        setData(json);
      } else {
        setUsingFallback(true);
      }
    }).
    catch(() => {
      setUsingFallback(true);
    }).
    finally(() => setLoading(false));
  }, []);

  const reviews = usingFallback ? fallbackReviews : data?.reviews ?? [];
  const overallRating = data?.rating ?? 5.0;
  const totalReviews = data?.total ?? null;

  return (
    <section id="reviews" className="py-20 sm:py-28 bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-accent text-sm font-semibold uppercase tracking-widest">Google Reviews</span>
          <h2 className="text-4xl sm:text-5xl font-black text-white mt-3 mb-4">What Clients Say</h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) =>
              <svg key={i} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>
            <span className="text-white font-bold text-xl">{overallRating.toFixed(1)}</span>
            <span className="text-sm text-[rgba(252,252,252,1)]">
              on Google{totalReviews ? ` · ${totalReviews} reviews` : ''}
            </span>
          </div>
        </div>

        {/* Loading State */}
        {loading &&
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {[...Array(6)].map((_, i) =>
          <div key={i} className="bg-white/8 border border-silver-DEFAULT/25 rounded-2xl p-6 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-white/10" />
                  <div className="flex-1">
                    <div className="h-3 bg-white/10 rounded w-24 mb-2" />
                    <div className="h-2 bg-white/10 rounded w-16" />
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => <div key={j} className="w-4 h-4 bg-white/10 rounded" />)}
                </div>
                <div className="space-y-2">
                  <div className="h-2 bg-white/10 rounded w-full" />
                  <div className="h-2 bg-white/10 rounded w-5/6" />
                  <div className="h-2 bg-white/10 rounded w-4/6" />
                </div>
              </div>
          )}
          </div>
        }

        {/* Reviews Grid */}
        {!loading &&
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {reviews.map((review, index) =>
          <div key={index} className="bg-white/8 border border-silver-DEFAULT/25 rounded-2xl p-6 hover:border-accent/30 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {review.photo ?
                <img
                  src={review.photo}
                  alt={review.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  referrerPolicy="no-referrer" /> :


                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold text-sm flex-shrink-0">
                        {review.initials}
                      </div>
                }
                    <div>
                      <div className="text-white font-semibold text-sm">{review.name}</div>
                      <div className="text-silver-DEFAULT text-xs">{review.date}</div>
                    </div>
                  </div>
                  <GoogleIcon />
                </div>
                <StarRating rating={review.rating} />
                <p className="text-silver-light text-sm mt-3 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
              </div>
          )}
          </div>
        }

        {/* Leave a Review CTA */}
        <div className="text-center">
          <p className="text-silver-light text-sm mb-4">Had a great experience? Let others know!</p>
          <a
            href="https://search.google.com/local/writereview?placeid=ChIJrRMon5tU2YARoExBMnTMmoc"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-gray-800 font-bold px-8 py-4 rounded-full text-sm transition-all duration-300 hover:scale-110 animate-pulse-glow hover:animate-none hover:shadow-[0_0_35px_rgba(255,255,255,0.8),0_0_70px_rgba(66,133,244,0.7),0_6px_20px_rgba(0,0,0,0.4)] border border-white/40"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)'
            }}>

            <GoogleIcon />
            <span className="tracking-wide">Leave a Google Review</span>
          </a>
        </div>
      </div>
    </section>);

}